import {observer} from "mobx-react";
import {ReactiveContentItem} from "../../schoolfeed/SchoolFeedContentItem";
import React, {RefObject} from "react";
import styles from "./styles/NewsModal.module.scss";
import FontAwesome from "../../utilities/FontAwesome";
import {withoutLocaleInURI} from "../../../internationalization/i18nURI";
import {useStores} from "../../../stores/useStores";
import {useTranslation} from 'react-i18next';

type ContentNode = Element | Text | null | undefined;

const NewsModalContentHeader = observer((props: {
    index: number,
    itemObj: ReactiveContentItem | IAccordionItem,
    navigationButtons?: React.ReactElement,
    closeFunction?: () => void,
    printRef: RefObject<ContentNode> | undefined,
    shareUrlPath: string,
}) => {
    const {modalStore} = useStores();
    const { t } = useTranslation('translation');

    async function onClick() {
        modalStore.addModal({
            type: "shareContentItem",
            url: withoutLocaleInURI("https://" + window.location.host + props.shareUrlPath),
            title: props.itemObj.title
        })
    }

    const printContent = () => {
        // Open a new tab
        const printWindow = window.open("", "_blank");

        if (!printWindow || !props?.printRef?.current) {
            console.error("Print reference is null or window could not be opened");
            return;
        }

        // Clone the content to print
        const contentToPrint = props.printRef.current.cloneNode(true);

        // Copy the entire head content to include styles
        const headContent = document.head.innerHTML;

        // Write full HTML into the new window
        printWindow.document.open();
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                ${headContent}
                <style>
                    * {
                        box-sizing: border-box;
                    }
                    html, body {
                        margin: 0;
                        padding: 0;
                        width: 100%;
                        height: 100%;
                    }
                    #print-container {
                        width: 100%;
                        height: auto;
                    }
                    #print-container > * {
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 auto !important;
                    }
                    @media print {
                        body {
                            -webkit-print-color-adjust: exact;
                        }
                    }
                </style>
            </head>
            <body>
                <div id="print-container"></div>
            </body>
        </html>
    `);
        printWindow.document.close();

        // Wait for the new window's content to be ready
        const interval = setInterval(() => {
            if (printWindow.document.readyState === 'complete') {
                clearInterval(interval);
                const container = printWindow.document.getElementById('print-container');
                if (container && contentToPrint) {
                    container.appendChild(contentToPrint);
                }

                // Delay slightly to ensure render before printing
                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                    // close window after cancelling
                    setTimeout(() => printWindow.close(), 500);
                }, 300);
            }
        }, 100);
    };

    return <>
        <header>
            <div>
                <button className={styles.buttonUnderlineEffect} onClick={() => {
                    if (props.closeFunction) {
                        props.closeFunction();
                    } else {
                        modalStore.removeTop();
                    }
                }} aria-label={t('Close')}>
                    <FontAwesome name={"fa-solid fa-times"}/><span className="notranslate">{t('Close')}</span>
                </button>
            </div>
            <div className={styles.navButtonsContainer}>
                {props.navigationButtons}
                <div className={styles.sharePrintButtons}>
                    <button onClick={onClick}><FontAwesome name={'fa-external-link-alt'}
                                                           prefix={'fas'}
                                                           aria-label={t('Copy link to content')}
                                                           title={t('Copy link to content')}/><span className="notranslate">&nbsp;{t('Share')}</span>
                    </button>
                    <button onClick={printContent}><FontAwesome name={'fa-print'}
                                                               prefix={'fas'}
                                                               aria-label={t('Print content')}
                                                               title={t('Print content')}/><span className="notranslate">&nbsp;{t('Print')}</span>
                    </button>
                </div>
            </div>
        </header>
    </>
})

export default NewsModalContentHeader;
